import { useState } from "react";
import { X } from "lucide-react";
import { useCredits, creditsDate } from "../hooks/useCredits";

/* Credits on screen (2026-10-08): the card in Settings and the strip above
   every screen. Both read useCredits, so they say the same number. Amber is
   attention (screen rule 5): the strip only appears when there is something
   to know — the two weeks' notice, running low, or out. */

function BuyLink({ url, children }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
      style={{ color: "var(--accent)", fontWeight: 600, textDecoration: "none", whiteSpace: "nowrap" }}>
      {children}
    </a>
  );
}

const NOTICE_KEY = "brill.creditsNoticeSeen";
const seenNotice = () => { try { return localStorage.getItem(NOTICE_KEY) === "1"; } catch { return false; } };
const markNotice = () => { try { localStorage.setItem(NOTICE_KEY, "1"); } catch { /* per-browser nicety only */ } };

export function CreditsBanner() {
  const { credits } = useCredits();
  const [hidden, setHidden] = useState(seenNotice);
  /* A tier that is never stopped has nothing to warn about (none today). */
  if (!credits || credits.enforced === false) return null;

  const count = <b style={{ color: "var(--text-primary)", fontWeight: 700 }}>{credits.remaining.toLocaleString()}</b>;
  let text = null;
  let dismissable = false;
  /* Her words for the notice, 2026-10-08 — and her ask that it look "much
     nicer… maybe transparent": no fill, readable secondary text, the count in
     bold, amber only as a small dot (rule 5: amber is attention, spent
     sparingly). Out of credits is the one state that earns amber text. */
  if (!credits.started) {
    if (hidden) return null;
    dismissable = true;
    text = <>Brill will have a usage cap as of {creditsDate(credits.startsOn)}. You can buy more credits as needed! You have {count} credits.</>;
  } else if (credits.out) {
    text = <span style={{ color: "var(--warn-text)", fontWeight: 600 }}>You've used all your Brill credits.</span>;
  } else if (credits.low) {
    text = <>You're running low — {count} Brill credits left.</>;
  }
  if (!text) return null;

  return (
    <div role="status" style={{
      display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
      padding: "7px 16px", fontSize: 12.5, lineHeight: 1.45,
      background: "transparent", color: "var(--text-secondary)",
      borderBottom: "1px solid var(--border-subtle, rgba(127,127,127,0.18))",
    }}>
      <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--warn-text)", flexShrink: 0 }} />
      <span style={{ flex: "1 1 220px", minWidth: 0 }}>{text}</span>
      <BuyLink url={credits.buyUrl}>Buy credits</BuyLink>
      {dismissable && (
        <button aria-label="Hide this notice" onClick={() => { markNotice(); setHidden(true); }}
          style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex", opacity: 0.6 }}>
          <X size={14} color="var(--text-secondary)" />
        </button>
      )}
    </div>
  );
}

export function CreditsCard({ card, heading }) {
  const { credits } = useCredits();
  if (!credits) return null;
  return (
    <div style={card}>
      <h3 style={heading}>Brill credits</h3>
      <p style={{ margin: "6px 0 2px", fontSize: 26, fontWeight: 700, color: credits.out ? "var(--warn-text)" : "var(--text-primary)" }}>
        {credits.remaining.toLocaleString()}
      </p>
      <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--text-muted)" }}>
        credits left · {credits.spent.toLocaleString()} used so far · {credits.tierLabel}
      </p>
      <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--text-secondary)" }}>
        Most messages use 1–5 credits; research in My Market uses more. Bought credits never expire.
        {!credits.started && ` Credits start counting toward a limit on ${creditsDate(credits.startsOn)}.`}
      </p>
      {credits.packs?.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {credits.packs.map((p) => (
            <span key={p.dollars} style={{ fontSize: 12, padding: "5px 10px", borderRadius: 8, background: "var(--bg-sunken, var(--bg-primary))", color: "var(--text-secondary)" }}>
              ${p.dollars} · {p.credits.toLocaleString()} credits
            </span>
          ))}
          {credits.buyUrl
            ? <BuyLink url={credits.buyUrl}>Top up →</BuyLink>
            : <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Ask Tevra to top up.</span>}
        </div>
      )}
    </div>
  );
}
